import {useLayoutStore, useOptionalLayoutCommitMessageGenerationConfig, type LayoutStoreState} from './LayoutStore'
import type {DiffAnnotation, Thread} from '@github-ui/diff-lines/conversation-types'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {useShallow} from 'zustand/react/shallow'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'
import {createLayoutMemoizedSelectorHook} from './create-layout-memoized-selector-hook'
import type {PullRequestNotices} from '../page-data/payloads/notices'
import {isCommittableSuggestion, type UnifiedBatchItem} from './slices/unified-batch'

/* Issue Comments */
export const useIssueComments = () => useLayoutStore(state => state.issueComments)
export const useIssueCommentsLoaded = () => useLayoutStore(state => state.issueCommentsLoaded)
export const useIssueCommentsActions = () => useLayoutStore(state => state.commentsPanelActions)

/* Pending Reviews */
export const usePendingReviewActions = () => useLayoutStore(s => s.pendingReviewActions)
export const usePendingReviews = () => useLayoutStore(s => s.pendingReview)
export const useIsCommentBatchPending = () => useLayoutStore(s => (s.pendingReview?.pendingReviewIDs?.length ?? 0) > 0)

export const useReviewSummaries = () => {
  return useLayoutStore(state => state.reviewSummaries)
}
export const useReviewSummariesActions = () => {
  return useLayoutStore(state => state.reviewSummariesActions)
}

/* Persisted Comments */
export const useStoredCommentAtPosition = (filePath: string, lineMarkersKey: LineKey | FileKey) =>
  useLayoutStore(state => state.storedCommentsByFile[filePath]?.[lineMarkersKey])
export const useGetUpdatePersistedComment = () =>
  useLayoutStore(s => s.commentsPersistenceActions.updateStoredCommentAtPosition)
export const useSetStoredCommentsForFile = () =>
  useLayoutStore(s => s.commentsPersistenceActions.setStoredCommentsForFile)

/* Comments Panel View */
export const useCommentsPanelViewMode = () => useLayoutStore(s => s.commentsPanelViewMode)
export const useCommentsPanelViewActions = () => useLayoutStore(s => s.commentsPanelViewActions)

/* Preferences */
export const usePreferenceActions = () => useLayoutStore(s => s.preferencesActions)

export const useCommentsPreference = () => useLayoutStore(s => s.commentsPreference)

/* Markers */
const emptyThreadsArray: Thread[] = []
const emptyAnnotationsArray: DiffAnnotation[] = []
const emptyMarkersObject = {
  threads: emptyThreadsArray,
  annotations: emptyAnnotationsArray,
  commentsPreference: CommentsPreference.Visible,
}

/**
 * Thread sizing info for accurate height estimation.
 * Contains reply count, collapsed state, and average body length.
 */
export type ThreadSizeInfo = {
  replyCount: number
  /** Whether the thread is visually collapsed (resolved threads default to collapsed) */
  isCollapsed: boolean
  /** Average body length of comments for height heuristics */
  avgBodyLength: number
}

export type ThreadSizeInfoMap = Map<number, ThreadSizeInfo>

// Memoized selector for flattening all threads - only recomputes when markers change
// Only includes threads with commentsData (threads without data are placeholder markers)
export const useAllThreads = createLayoutMemoizedSelectorHook([s => s.markers], markers =>
  Object.values(markers || {}).flatMap(files =>
    Object.values(files).flatMap(markersInLine =>
      (markersInLine?.threads ?? emptyThreadsArray).filter(thread => thread.commentsData),
    ),
  ),
)

/**
 * Memoized selector that computes sizing info for all threads.
 * Returns a Map from thread ID to ThreadSizeInfo (reply count + collapsed state + avg body length).
 * This is used for more accurate height estimation in virtualized diff lists.
 */
export const useThreadSizeInfoMap = createLayoutMemoizedSelectorHook([useAllThreads.selector], threads => {
  const sizeInfoMap = new Map<number, ThreadSizeInfo>()
  for (const thread of threads) {
    const threadId = parseInt(thread.id, 10)
    if (isNaN(threadId)) continue

    const comments = thread.commentsData?.comments ?? []
    const commentCount = comments.length || thread.commentCount || 1

    // Calculate average body length for height heuristics
    let totalBodyLength = 0
    for (const comment of comments) {
      totalBodyLength += comment.body?.length ?? 0
    }
    const avgBodyLength = comments.length > 0 ? Math.round(totalBodyLength / comments.length) : 0

    // Resolved threads default to collapsed (user can expand via localStorage)
    // We don't have access to localStorage state here, so we assume default behavior
    const isCollapsed = thread.isResolved ?? false

    sizeInfoMap.set(threadId, {
      replyCount: Math.max(0, commentCount - 1),
      isCollapsed,
      avgBodyLength,
    })
  }
  return sizeInfoMap
})

export const useMarkersActions = () => useLayoutStore(s => s.markersActions)
export const useAllMarkers = () => useLayoutStore(s => s.markers)

export const useGetThread = () => useLayoutStore(s => s.getThread)
export const useGetThreadFromThreadID = () => useLayoutStore(s => s.getThreadFromThreadID)
export const useThread = (threadId: string, filePath: string, lineMarkersKey: LineKey | FileKey) =>
  useLayoutStore(s => s.markers?.[filePath]?.[lineMarkersKey]?.threads.find(thread => thread.id === threadId))
export const useMarkersAtPosition = (filePath: string, lineMarkersKey: LineKey | FileKey) =>
  useLayoutStore(
    useShallow(s => {
      const lineMarkers = s.markers?.[filePath]?.[lineMarkersKey]
      //filter out any threads that are only for the side panel
      const threads = lineMarkers?.threads ?? emptyThreadsArray
      const annotations = lineMarkers?.annotations ?? emptyAnnotationsArray
      const commentsPreference = s.commentsPreference

      if (!threads.length && !annotations.length) {
        return emptyMarkersObject
      }
      return {threads, annotations, commentsPreference}
    }),
  )

export const useUnresolvedThreadCount = () =>
  useLayoutStore(s => {
    const allThreads = useAllThreads.selector(s)
    let unresolvedCount = 0
    for (const thread of allThreads) {
      if (!thread) continue
      if (!thread.isResolved) unresolvedCount++
    }
    return unresolvedCount
  })

export const useThreadCount = (ids?: number[]) =>
  useLayoutStore(s => {
    const allThreads = useAllThreads.selector(s)
    const idsSet = ids ? new Set(ids) : null
    let count = 0
    for (const thread of allThreads) {
      if (!thread) continue
      if (idsSet && !idsSet.has(parseInt(thread.id))) continue
      if (thread.isResolved || thread.isOutdated || thread.positioning?.isOutdated) continue
      count++
    }
    return count
  })

export const useMarkers = (filePath: string) =>
  useLayoutStore(
    useShallow(s => {
      const markers = s.markers?.[filePath]
      return markers
    }),
  )

export const useThreads = (ids?: number[]) =>
  useLayoutStore(
    useShallow(s => {
      const allThreads = useAllThreads.selector(s)
      if (!ids) return allThreads

      // Filter by IDs - cheap since allThreads is already memoized
      const idsSet = new Set(ids)
      return allThreads.filter(thread => idsSet.has(Number(thread.id)))
    }),
  )

// Memoized selector for flattening all annotations - only recomputes when markers change
export const useAnnotations = createLayoutMemoizedSelectorHook([s => s.markers], markers =>
  Object.values(markers || {}).flatMap(files =>
    Object.values(files).flatMap(markersInLine => markersInLine?.annotations ?? emptyAnnotationsArray),
  ),
)
export const useAnnotationsPageInfo = () => useLayoutStore(s => s.annotationsPageInfo)

/* Pull Request Data */
export const useLayoutPullRequest = () => useLayoutStore(s => s.layoutPullRequest)
export const usePullRequestBasePath = () => useLayoutStore(s => s.layoutPullRequest.basePath)
export const useLayoutPullRequestId = () => useLayoutStore(s => s.layoutPullRequest.id)
export const useLayoutPullRequestNumber = () => useLayoutStore(s => s.layoutPullRequest.number)
export const useLayoutPullRequestTitle = () => useLayoutStore(s => s.layoutPullRequest.title ?? '')
export const useLayoutAuthorLogin = () => useLayoutStore(s => s.layoutPullRequest.author.login)
export const useLayoutBaseRepository = () => useLayoutStore(useShallow(s => s.layoutPullRequest.baseRepository))
export const useOptionalCommitMessageGenerationConfig = () => useOptionalLayoutCommitMessageGenerationConfig()

export const useLayoutCanComment = () => useLayoutStore(s => s.layoutCurrentUser.canComment)
export const useLayoutCanEditTitle = () => useLayoutStore(s => s.layoutCurrentUser.canEditTitle)

/**
 * True when the PR's head branch lives in a different repository than its base
 * (the classic fork contribution). Only a positive mismatch counts, so payloads
 * missing head repository data are treated as same-repo.
 */
export const getIsPullRequestFromFork = (s: LayoutStoreState) => {
  const {headRepositoryOwnerLogin, headRepositoryName, baseRepository} = s.layoutPullRequest
  if (!headRepositoryOwnerLogin || !headRepositoryName) return false
  return headRepositoryOwnerLogin !== baseRepository.ownerLogin || headRepositoryName !== baseRepository.name
}
export const useIsPullRequestFromFork = () => useLayoutStore(getIsPullRequestFromFork)

// Only an explicit denial gates Fix with Copilot so older or incomplete payloads fail open.
// Copilot coding agent can't push to a contributor's fork, so fork PRs are denied outright:
// letting it run there opens unrelated PRs against the base repository's default branch.
export const useIsFixWithCopilotAvailable = () =>
  useLayoutStore(s => s.layoutCurrentUser.canFixWithCopilot !== false && !getIsPullRequestFromFork(s))
export const useIsNoticeDismissed = (name: PullRequestNotices) =>
  useLayoutStore(s => {
    const notice = s.layoutCurrentUser.notices.find(n => n.name === name)
    return !notice || notice.dismissed
  })
export const useUserNoticesActions = () => useLayoutStore(s => s.layoutCurrentUserActions)

/* Diff Path Order */
const emptyDiffPathOrderMap = new Map<string, number>()
export const useDiffPathOrderMap = () => useLayoutStore(s => s.diffPathOrderMap ?? emptyDiffPathOrderMap)
export const useLayoutStoreActions = () => useLayoutStore(s => s.layoutStoreActions)
export const useLayoutStoreSeeded = () => useLayoutStore(s => s.layoutStoreSeeded)

/* Diff Navigation Callbacks (registered by files-changed route) */
export const useNavigateToComment = () => useLayoutStore(s => s.filesChangedCallbacks?.navigateToComment ?? null)
export const useNavigateToAnnotation = () => useLayoutStore(s => s.filesChangedCallbacks?.navigateToAnnotation ?? null)
export const useOnCommentThreadDeleted = () =>
  useLayoutStore(s => s.filesChangedCallbacks?.onCommentThreadDeleted ?? null)

/* Unresolved Conversation Count (server-provided, kept in sync via mutations) */
export const useUnresolvedConversationCount = () => useLayoutStore(s => s.unresolvedConversationCount)
export const useUnresolvedConversationCountActions = () =>
  useLayoutStore(
    useShallow(s => ({
      hydrateMarkerCounts: s.markerCountsActions.hydrateMarkerCounts,
      incrementUnresolvedConversationCount: s.markerCountsActions.incrementUnresolvedConversationCount,
      decrementUnresolvedConversationCount: s.markerCountsActions.decrementUnresolvedConversationCount,
    })),
  )

/* Annotations Count (server-provided from marker counts) */
export const useAnnotationsCount = () => useLayoutStore(s => s.annotationsCount)
export const useMarkerCountsActions = () => useLayoutStore(s => s.markerCountsActions)

/* Comparison Outdated */
export const useIsComparisonOutdated = () => useLayoutStore(s => s.isComparisonOutdated)
export const useComparisonActions = () => useLayoutStore(s => s.comparisonOutdatedActions)

/* Unified Batch */
export type BatchComposition = 'empty' | 'suggestion-only' | 'mixed' | 'no-suggestion'

export function getBatchComposition(items: UnifiedBatchItem[]): BatchComposition {
  if (items.length === 0) return 'empty'

  let committableSuggestionCount = 0
  for (const item of items) {
    if (isCommittableSuggestion(item)) {
      committableSuggestionCount++
    }
  }

  if (committableSuggestionCount === items.length) return 'suggestion-only'
  if (committableSuggestionCount === 0) return 'no-suggestion'

  return 'mixed'
}

export const useUnifiedBatch = () => useLayoutStore(useShallow(s => s.unifiedBatch))
export const useUnifiedBatchActions = () => useLayoutStore(s => s.unifiedBatchActions)
export const useUnifiedBatchCount = () => useLayoutStore(s => s.unifiedBatch.items.length)
export const useBatchRevalidatedHeadSha = () => useLayoutStore(s => s.unifiedBatch.revalidatedHeadSha)
export const useBatchComposition = createLayoutMemoizedSelectorHook([s => s.unifiedBatch.items], items =>
  getBatchComposition(items),
)
export const useIsInUnifiedBatch = (id: {commentDatabaseId: number; suggestionIndex?: number}) =>
  useLayoutStore(s =>
    s.unifiedBatch.items.some(
      item =>
        item.commentDatabaseId === id.commentDatabaseId &&
        (item.suggestionIndex ?? null) === (id.suggestionIndex ?? null),
    ),
  )
export const useThreadHasBatchedSuggestion = (threadId: number | null | undefined) =>
  useLayoutStore(
    s => threadId != null && s.unifiedBatch.items.some(item => item.hasSuggestion && item.threadId === threadId),
  )
export const useAllHaveSuggestion = createLayoutMemoizedSelectorHook(
  [s => s.unifiedBatch.items],
  items => items.length > 0 && items.every(item => item.hasSuggestion),
)
export const useHasOutdatedItems = createLayoutMemoizedSelectorHook([s => s.unifiedBatch.items], items =>
  items.some(item => item.isOutdated),
)
